import {
  MAX_MATERIAL_CHARS,
  MAX_MATERIALS,
  MAX_MATERIALS_TOTAL_CHARS,
  type MaterialDoc,
  type MaterialKind,
} from '../shared/contract';
import { extractMaterial } from './api';

export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;
export const ACCEPTED_UPLOADS = '.pdf,.docx,.txt,.md,.png,.jpg,.jpeg';

export interface UploadedMaterial extends MaterialDoc {
  truncated?: boolean;
}

export function totalChars(materials: ReadonlyArray<MaterialDoc>): number {
  return materials.reduce((sum, material) => sum + material.text.length, 0);
}

export function newMaterialId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return `up-${crypto.randomUUID()}`;
  return `up-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function extension(name: string): string {
  const match = /\.([a-z0-9]+)$/iu.exec(name);
  return (match?.[1] ?? '').toLowerCase();
}

/** Reads one file into a material, enforcing the contract limits against the current list. */
export async function readMaterialFile(
  file: File,
  kind: MaterialKind,
  existing: ReadonlyArray<MaterialDoc>,
): Promise<UploadedMaterial> {
  if (existing.length >= MAX_MATERIALS) {
    throw new Error(`You can attach at most ${MAX_MATERIALS} documents. Remove one first.`);
  }
  if (file.size > MAX_UPLOAD_BYTES) throw new Error(`${file.name} is larger than 8 MB.`);
  const ext = extension(file.name);
  if (!['pdf', 'docx', 'txt', 'md', 'png', 'jpg', 'jpeg'].includes(ext)) {
    throw new Error(`${file.name}: use PDF, DOCX, TXT, MD, PNG or JPG.`);
  }

  let text: string;
  let truncated = false;
  let name = file.name;
  if (ext === 'txt' || ext === 'md') {
    text = (await file.text()).replace(/\r\n?/gu, '\n').trim();
  } else {
    const extracted = await extractMaterial(file);
    text = extracted.text.trim();
    truncated = extracted.truncated;
    name = extracted.name || file.name;
  }
  if (!text) throw new Error(`No readable text was found in ${file.name}.`);

  if (text.length > MAX_MATERIAL_CHARS) {
    text = text.slice(0, MAX_MATERIAL_CHARS);
    truncated = true;
  }
  const remaining = MAX_MATERIALS_TOTAL_CHARS - totalChars(existing);
  if (remaining <= 0) {
    throw new Error(`The ${MAX_MATERIALS_TOTAL_CHARS.toLocaleString()}-character total limit is reached. Remove a document first.`);
  }
  if (text.length > remaining) {
    text = text.slice(0, remaining);
    truncated = true;
  }
  return { id: newMaterialId(), kind, name, text, truncated };
}
